import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { api, unwrap } from "../api/client";
import type { components } from "../api/schema";

type Lane = components["schemas"]["TimelineLane"];

const hms = (ms: number) =>
  new Date(ms).toISOString().slice(0, 19).replace("T", " ");

/** The lane's recordings during `range`. */
function during(lane: Lane, range: { from: number; to: number }) {
  return lane.files.filter((f) => {
    const start = Date.parse(f.start_utc);
    const end = f.end_utc ? Date.parse(f.end_utc) : start;
    return start < range.to && end > range.from;
  });
}

/**
 * A synchronised export (D6) from the case on view: the chosen sources'
 * recordings during `range`, with a clip of each cut to it, all starting
 * together; requested for approval like any export.
 */
export function SyncExport({
  caseId,
  range,
  lanes,
  onClose,
}: {
  caseId: string;
  range: { from: number; to: number };
  lanes: Lane[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const inRange = lanes
    .map((l) => ({ lane: l, files: during(l, range) }))
    .filter((l) => l.files.length > 0);
  const key = (l: Lane) => `${l.library_id}/${l.source ?? ""}`;
  const [left, setLeft] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState("");
  const chosen = inRange.filter((l) => !left.has(key(l.lane)));
  const fileIds = chosen.flatMap((l) => l.files.map((f) => f.id));
  const cameras = chosen.filter((l) => l.files.some((f) => f.kind === "video"));
  const heard = chosen.filter((l) =>
    l.files.some(
      (f) => f.kind === "audio" || (f.kind === "video" && f.has_audio),
    ),
  );
  const [together, setTogether] = useState(false);
  // A dedicated recorder first: it usually hears best.
  const [sound, setSound] = useState<string>(
    () =>
      (
        inRange.find(({ files }) => files.some((f) => f.kind === "audio")) ??
        inRange.find(({ files }) => files.some((f) => f.has_audio))
      )?.lane.source ?? "",
  );
  const canTogether = cameras.length >= 1 && cameras.length <= 4;
  const request = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/cases/{id}/exports", {
          params: { path: { id: caseId } },
          body: {
            reason,
            file_ids: fileIds,
            range: {
              from: new Date(range.from).toISOString(),
              to: new Date(range.to).toISOString(),
            },
            side_by_side:
              together && canTogether
                ? {
                    sound_source: heard.some((l) => l.lane.source === sound)
                      ? sound
                      : null,
                  }
                : null,
          },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cases", caseId, "exports"] });
    },
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Export these times</DialogTitle>
      <DialogContent>
        {request.isSuccess ? (
          <Alert severity="success">
            Requested. Someone with approval rights approves it on the
            case&rsquo;s page, where you then download it.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {hms(range.from)} to {hms(range.to)} UTC. Each chosen recording is
              included whole, as evidence, with a clip cut to these times; the
              clips all start together, so they play in step.
            </Typography>
            <FormGroup aria-label="Sources">
              {inRange.map(({ lane, files }) => (
                <FormControlLabel
                  key={key(lane)}
                  control={
                    <Checkbox
                      checked={!left.has(key(lane))}
                      onChange={(e) => {
                        const next = new Set(left);
                        if (e.target.checked) next.delete(key(lane));
                        else next.add(key(lane));
                        setLeft(next);
                      }}
                    />
                  }
                  label={`${lane.source ?? "Unidentified"} (${files.length} recording${files.length === 1 ? "" : "s"})`}
                />
              ))}
            </FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={together && canTogether}
                  disabled={!canTogether}
                  onChange={(e) => setTogether(e.target.checked)}
                />
              }
              label={
                canTogether
                  ? `Also put the ${cameras.length === 1 ? "camera" : `${cameras.length} cameras`} side by side in one video, labelled, under the UTC time`
                  : "Side by side takes one to four cameras"
              }
            />
            {together && canTogether && (
              <TextField
                select
                size="small"
                label="Its sound from"
                value={heard.some((l) => l.lane.source === sound) ? sound : ""}
                onChange={(e) => setSound(e.target.value)}
                sx={{ minWidth: 240, mt: 1 }}
                slotProps={{
                  select: { displayEmpty: true },
                  inputLabel: { shrink: true },
                }}
              >
                <MenuItem value="">No sound</MenuItem>
                {heard.map(({ lane }) => (
                  <MenuItem key={key(lane)} value={lane.source ?? ""}>
                    {lane.source ?? "Unidentified"}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {inRange.length === 0 && (
              <Alert severity="info">
                Nothing in the case was recording then.
              </Alert>
            )}
            <TextField
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              placeholder="For example, disclosure to the CPS"
            />
            {request.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {request.error.message}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {request.isSuccess ? "Close" : "Cancel"}
        </Button>
        {!request.isSuccess && (
          <Button
            variant="contained"
            disabled={
              !reason.trim() || fileIds.length === 0 || request.isPending
            }
            onClick={() => request.mutate()}
          >
            Request export of {chosen.length} source
            {chosen.length === 1 ? "" : "s"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
